/**
 * Permission Constants
 * Define all permissions here for easy management and consistency across the app.
 *
 * Usage:
 *   import { PERMISSIONS } from 'constants/permissions'
 *   <Can do={PERMISSIONS.WORKORDER.CREATE}> ... </Can>
 *   const canEdit = useCan(PERMISSIONS.CONTRACT.EDIT)
 */

export const PERMISSIONS = {
  // Dashboard
  DASHBOARD: {
    VIEW: 'dashboard_view',
  },

  // Contracts
  CONTRACT: {
    VIEW: 'contract_view',
    CREATE: 'contract_create',
    EDIT: 'contract_edit',
    DELETE: 'contract_delete',
  },

  // Work Orders
  WORKORDER: {
    VIEW: 'workorder_view',
    CREATE: 'workorder_create',
    EDIT: 'workorder_edit',
    DELETE: 'workorder_delete',
    COMPLETE: 'workorder_complete',
    APPROVE: 'workorder_approve',
    PRINT: 'workorder_print',
  },

  // Proposals
  PROPOSAL: {
    VIEW: 'proposal_view',
    CREATE: 'proposal_create',
    EDIT: 'proposal_edit',
    DELETE: 'proposal_delete',
    SEND: 'proposal_send',
    APPROVE: 'proposal_approve',
    REJECT: 'proposal_reject',
    BUDGET_APPROVAL: 'proposal_budget_approval', // Parameterized: value in Euros
    CREATE_WORKORDER: 'proposal_create_workorder', // PROTEC-951: work order from an approved proposal
    CHANGE_ELIN_APPROVED: 'proposal_approved_change_elin', // ELIN of a line on an approved proposal
  },

  // Inventory
  INVENTORY: {
    VIEW: 'inventory_view',
    CREATE: 'inventory_create',
    EDIT: 'inventory_edit',
    DELETE: 'inventory_delete',
  },
  INVENTORY_CLASS: {
    VIEW: 'inventoryclass_view',
    MANAGE: 'inventoryclass_manage',
  },
  MAIN_INVENTORY: {
    VIEW: 'maininventory_view',
    MANAGE: 'maininventory_manage',
  },
  DRAFT_INVENTORY: {
    VIEW: 'draftinventory_view',
  },

  // Parts & Supply
  PARTS: {
    VIEW: 'parts_view',
    MANAGE: 'parts_manage',
  },
  PART_ORDER: {
    VIEW: 'partorder_view',
    MANAGE: 'partorder_manage',
  },
  PART_TRANSFER: {
    VIEW: 'parttransfer_view',
    MANAGE: 'parttransfer_manage',
  },

  // Technicians
  TECHNICIAN: {
    VIEW: 'technician_view',
    MANAGE: 'technician_manage',
    REPORT_VIEW: 'technician_report_view',
    REPORT_MANAGE: 'technician_report_manage',
    PARTS_VIEW: 'technician_parts_view',
    PARTS_MANAGE: 'technician_parts_manage',
  },

  // Invoicing
  INVOICE: {
    VIEW: 'invoice_view',
    CREATE: 'invoice_create',
    EDIT: 'invoice_edit',
    DELETE: 'invoice_delete',
  },

  // Contacts & Suppliers
  CONTACT: {
    VIEW: 'contact_view',
    MANAGE: 'contact_manage',
  },
  SUPPLIER: {
    VIEW: 'supplier_view',
    MANAGE: 'supplier_manage',
  },

  // Locations
  LOCATION: {
    VIEW: 'location_view',
    MANAGE: 'location_manage',
  },

  // Cost Management
  COST_TYPE: {
    VIEW: 'costtype_view',
    MANAGE: 'costtype_manage',
  },
  CLIN_BUDGET: {
    VIEW: 'clinbudget_view',
    MANAGE: 'clinbudget_manage',
  },
  // PROTEC-950: export / import of intervals, contact persons, technicians, inventory
  // classes, CLIN & Budget and ELIN - one permission for all
  BASIC_DATA_EXCHANGE: {
    USE: 'basicdata_exchange',
  },

  // Reports
  REPORT: {
    VIEW: 'report_view',
    WORKORDER: 'report_workorder',
    INVENTORY: 'report_inventory',
    INVOICE: 'report_invoice',
    TECHNICIAN: 'report_technician',
    BUDGET: 'report_budget',
    BILLING: 'report_billing',
    OFFICE_NOTES: 'report_office_notes',
  },

  // Protocols
  PROTOCOL: {
    VIEW: 'protocol_view',
    MANAGE: 'protocol_manage',
    ASSIGNMENT: 'protocol_assignment_manage',
  },

  // Quality Control
  QUALITY: {
    VIEW: 'quality_view',
    MANAGE: 'quality_manage',
  },

  // Next Controls (PM Scheduling)
  NEXT_CONTROL: {
    VIEW: 'nextcontrol_view',
  },

  // Companies
  COMPANY: {
    VIEW: 'company_view',
    MANAGE: 'company_manage',
  },

  // User & Admin
  USER: {
    VIEW: 'user_view',
    MANAGE: 'user_manage',
    PASSWORD_MANAGE: 'user_password_manage',
  },
  ROLE: {
    MANAGE: 'role_manage',
  },
  PERMISSION: {
    MANAGE: 'permission_manage',
  },

  // Tools
  TOOLS: {
    PROJECT_COPY: 'tools_project_copy',
  },

  // Activity Log
  ACTIVITY_LOG: {
    VIEW: 'activitylog_view',
  },

  // System Admin
  REPAIR_LIMIT: {
    VIEW: 'repairlimit_view',
    MANAGE: 'repairlimit_manage',
  },
  MATERIAL_PRICE: {
    VIEW: 'materialprice_view',
    MANAGE: 'materialprice_manage',
  },
  SERVICE_REQUEST: {
    VIEW: 'servicerequest_view',
    MANAGE: 'servicerequest_manage',
  },
  EMAIL: {
    MANAGE: 'email_manage',
  },
  NOTIFICATION: {
    MANAGE: 'notification_manage',
  },
  INTERVAL: {
    MANAGE: 'interval_manage',
  },

  // Customer Portal
  CUSTOMER: {
    VIEW: 'customer_view',
    DASHBOARD: 'customer_dashboard',
    WO_REQUEST: 'customer_wo_request',
    PROPOSAL_REQUEST: 'customer_proposal_request',
    PROPOSAL_APPROVE: 'customer_proposal_approve',
  },
}

/**
 * Redux Selector - Check if a user has a specific permission
 * @param {string} permissionSlug - The permission slug to check
 * @returns {Function} Selector function that takes Redux state
 *
 * Usage: const canCreate = useSelector(checkPermission(PERMISSIONS.WORKORDER.CREATE))
 */
export const checkPermission = permissionSlug => state => {
  const permissions = state.user?.permissions || []
  return permissions.some(p => (typeof p === 'string' ? p : p?.slug) === permissionSlug)
}

/**
 * Redux Selector - Get parameter value for a parameterized permission
 * @param {string} permissionSlug - The permission slug
 * @returns {Function} Selector function that returns the parameter value or null
 *
 * Usage: const budgetLimit = useSelector(getPermissionValue(PERMISSIONS.PROPOSAL.BUDGET_APPROVAL))
 */
export const getPermissionValue = permissionSlug => state => {
  const permissions = state.user?.permissions || []
  const perm = permissions.find(p => (typeof p === 'string' ? false : p?.slug === permissionSlug))
  return perm?.parameterValue || null
}
